import { Button, Check, X } from '@chain-zen/ui'

import type { TExternalTrackingLinkSource } from '../../../types/tracking'
import ProviderIcon from '../provider-icon'
import { TrackingSourceRow } from '../tracking-source-row'
import { TrackingTimeDisplay } from '../tracking-time-display'
import { RemoveTrackingLink } from './remove-tracking-link'

interface TrackingLinkProps {
  shipmentID: string
  trackingSourceData: TExternalTrackingLinkSource
}

/**
 * Displays the details for an external tracking link
 */
export const TrackingLinkInfo = (props: TrackingLinkProps) => {
  const { trackingSourceData, shipmentID } = props

  const isSupported = trackingSourceData.metadata?.isSupported
  const StatusIcon = isSupported ? Check : X
  const lastLocation =
    trackingSourceData?.locations && trackingSourceData.locations.length > 0
      ? trackingSourceData.locations[trackingSourceData.locations.length - 1]
      : null

  const handleOpenTrackingLink = () => {
    if (!trackingSourceData?.metadata?.url) {
      return
    }
    window.open(trackingSourceData.metadata.url, '_blank', 'noopener,noreferrer')
  }

  return (
    <TrackingSourceRow
      className="px-2 group"
      actions={
        <>
          <Button
            variant="outline"
            size="xs"
            className="h-7 whitespace-nowrap px-3"
            onClick={handleOpenTrackingLink}
            disabled={!trackingSourceData?.metadata?.url}
          >
            View Tracking Link
          </Button>
          <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity relative">
            <RemoveTrackingLink shipmentID={shipmentID} trackingURL={trackingSourceData.metadata?.url || ''} />
          </div>
        </>
      }
    >
      {/* Tracking Link Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 flex-wrap">
          <StatusIcon
            className={`h-3 w-3 shrink-0 self-center ${
              isSupported ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'
            }`}
          />
          <ProviderIcon provider={trackingSourceData.metadata?.provider || null} size={3} />
          <span className="text-sm font-medium text-foreground">
            Tracking link: {trackingSourceData?.metadata?.provider || 'Not supported'}
          </span>
          {!!lastLocation && (
            <TrackingTimeDisplay
              filterReason={trackingSourceData.filterReason}
              lastLocationCity={lastLocation.city}
              lastLocationFetchedAt={trackingSourceData.lastLocationFetchedAt}
              lastLocationStateCode={lastLocation.stateCode}
              lastLocationTimestamp={lastLocation.timestamp}
              wrapper="div"
              className="text-xs text-muted-foreground"
            />
          )}
        </div>
      </div>
    </TrackingSourceRow>
  )
}
